import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiArrowRight, FiTrendingUp, FiClock, FiDollarSign, FiUsers, FiChevronDown, FiChevronUp, FiDownload, FiMic, FiFileText, FiCpu, FiPlus, FiMinus, FiExternalLink } from 'react-icons/fi';
import api from '../utils/api';

const ICON_MAP = {
  FiTrendingUp,
  FiClock,
  FiDollarSign,
  FiUsers,
};

const HOW_IT_WORKS_ICONS = { FiMic, FiFileText, FiCpu };

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 dark:border-gray-800">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-4 text-left text-gray-900 dark:text-white font-medium text-sm hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <span>{question}</span>
        {open ? <FiMinus className="w-4 h-4 flex-shrink-0 ml-4" /> : <FiPlus className="w-4 h-4 flex-shrink-0 ml-4" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ProductContent({ data, tags, project }) {
  const screenshot = data.screenshot || null;
  const howItWorks = data.howItWorks || [];
  const faq = data.faq || [];
  const stats = data.stats || [];

  return (
    <>
      {/* 1. Hero Screenshot */}
      {screenshot && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-14"
        >
          <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
            {/* macOS title bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600" />
              <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600" />
              <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600" />
              <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-medium">{project.headline}</span>
            </div>
            <img
              src={screenshot}
              alt={`${project.headline} screenshot`}
              className="w-full"
            />
          </div>
        </motion.div>
      )}

      {/* CTA — Hero position */}
      {(data.links?.github || data.links?.live) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap items-center gap-4 mb-14"
        >
          {data.links?.live && (
            <a
              href={data.links.live}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-modern inline-flex items-center space-x-2 text-sm"
            >
              <FiExternalLink className="w-4 h-4" />
              <span>Visit Live Site</span>
            </a>
          )}
          {data.links?.github && (
            <a
              href={data.links.github}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-modern inline-flex items-center space-x-2 text-sm"
            >
              <FiDownload className="w-4 h-4" />
              <span>Download {data.version || ''}</span>
            </a>
          )}
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {[data.platform, data.requirements].filter(Boolean).join(' · ')}
          </span>
        </motion.div>
      )}

      {/* 2. How It Works */}
      {howItWorks.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mb-14"
        >
          <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-6">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {howItWorks.map((step, i) => {
              const Icon = HOW_IT_WORKS_ICONS[step.icon] || FiArrowRight;
              return (
                <div key={i} className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 mb-4">
                    <Icon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                    Step {i + 1}
                  </p>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* 6. Stats / Social Proof */}
      {stats.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-14"
        >
          <div className={`grid gap-4 ${stats.length === 1 ? 'grid-cols-1 max-w-xs' : stats.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {stats.map((stat, i) => (
              <div key={i} className="text-center p-5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Features */}
      {data.features && data.features.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="mb-14"
        >
          <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4">
            Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.features.map((feature) => (
              <div key={feature} className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <span className="text-gray-400 dark:text-gray-500 mt-0.5 font-bold">—</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Tech Stack */}
      {data.techStack && data.techStack.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-14"
        >
          <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
            Built With
          </h2>
          <div className="flex flex-wrap gap-2">
            {data.techStack.map((tech) => (
              <span
                key={tech}
                className="text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md font-mono border border-gray-200 dark:border-gray-700"
              >
                {tech}
              </span>
            ))}
          </div>
        </motion.div>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="mb-14"
        >
          <h2 className="text-sm font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4">
            FAQ
          </h2>
          <div className="border-t border-gray-200 dark:border-gray-800">
            {faq.map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </motion.div>
      )}

      {/* Bottom CTA */}
      {(data.links?.github || data.links?.live) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="pt-10 border-t border-gray-100 dark:border-gray-800 text-center"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Ready to try {project.headline}?
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {[data.platform, data.requirements, data.version].filter(Boolean).join(' · ')}
          </p>
          {data.links?.live && (
            <a
              href={data.links.live}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-modern inline-flex items-center space-x-2 text-sm"
            >
              <FiExternalLink className="w-4 h-4" />
              <span>Visit Live Site</span>
            </a>
          )}
          {data.links?.github && (
            <a
              href={data.links.github}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-modern inline-flex items-center space-x-2 text-sm"
            >
              <FiDownload className="w-4 h-4" />
              <span>Download Now</span>
            </a>
          )}
        </motion.div>
      )}
    </>
  );
}

function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expandedSections, setExpandedSections] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get(`/projects/slug/${slug}`);
        setProject(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          setNotFound(true);
        }
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-gray-300 dark:border-gray-600 border-t-black dark:border-t-white rounded-full"
        />
      </div>
    );
  }

  if (notFound || !project) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Project not found</h1>
          <Link to="/projects" className="btn-modern-outline inline-flex items-center space-x-2 text-sm">
            <FiArrowLeft className="w-4 h-4" />
            <span>Back to Projects</span>
          </Link>
        </div>
      </div>
    );
  }

  const data = JSON.parse(project.data || '{}');
  const tags = JSON.parse(project.tags || '[]');
  const isCaseStudy = project.type === 'case-study';

  const metrics = isCaseStudy
    ? (data.metrics || []).map(m => ({ ...m, icon: ICON_MAP[m.icon] || FiTrendingUp }))
    : [];
  const sections = isCaseStudy ? (data.sections || []) : [];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <Link
          to="/projects"
          className="inline-flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-10"
        >
          <FiArrowLeft className="w-4 h-4" />
          <span>All Projects</span>
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {project.company}
            </span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">{project.role}</span>
            {project.duration && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">{project.duration}</span>
              </>
            )}
            {!isCaseStudy && data.platform && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">{data.platform}</span>
              </>
            )}
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight mb-2">
            {project.headline}
          </h1>
          {!isCaseStudy && data.tagline && (
            <p className="text-lg text-gray-500 dark:text-gray-400">{data.tagline}</p>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md"
              >
                {tag}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-10"
        >
          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
            {project.summary}
          </p>
        </motion.div>

        {/* === Case Study Content === */}
        {isCaseStudy && (
          <>
            {/* Metrics */}
            {metrics.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className={`grid gap-4 mb-10 ${metrics.length === 1 ? 'grid-cols-1 max-w-xs' : metrics.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}
              >
                {metrics.map((m, i) => {
                  const Icon = m.icon;
                  return (
                    <div key={i} className="flex flex-col items-center text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                      <Icon className="w-5 h-5 text-gray-500 dark:text-gray-400 mb-2" />
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{m.value}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{m.label}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{m.delta}</p>
                    </div>
                  );
                })}
              </motion.div>
            )}

            {/* Sections */}
            {sections.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <button
                  onClick={() => setExpandedSections(!expandedSections)}
                  className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors mb-6"
                >
                  <span>{expandedSections ? 'Hide full case study' : 'Read full case study'}</span>
                  {expandedSections ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                </button>

                <AnimatePresence>
                  {expandedSections && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-8 mb-10">
                        {sections.map((section) => (
                          <div key={section.title}>
                            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
                              {section.title}
                            </h3>
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                              {section.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* CTA */}
            <div className="pt-8 border-t border-gray-100 dark:border-gray-800">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Want to see results like this for your business?
              </p>
              <Link to="/contact" className="btn-modern inline-flex items-center space-x-2 text-sm">
                <span>Let's Talk</span>
                <FiArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}

        {/* === Product Content === */}
        {!isCaseStudy && (
          <ProductContent data={data} tags={tags} project={project} />
        )}
      </div>
    </div>
  );
}

export default ProjectDetail;
